import { environment } from 'src/environments/environment';
import {
  AnalyticsProvider,
  EventProperties,
  UserIdentity,
  UserProperties,
} from './analytics.types';

/**
 * Third sink: our own backend.
 *
 * WHY, GIVEN MIXPANEL ALREADY RECEIVES ALL OF THIS
 * ──────────────────────────────────────────────────────────────────────────
 * Mixpanel can show a journey inside Mixpanel. It cannot show one on OUR
 * panel beside the project it belongs to, because it cannot join to
 * `projects`, `comm_*` or the contributor DB — and half the funnel (TOE
 * signed, vendor assigned, payments) happens server-side where Mixpanel has
 * no visibility at all. This is the copy we can join.
 *
 * It is a SINK, not a dependency: nothing in the app reads from it, and every
 * failure path below ends in a silent drop. An analytics backend being down
 * must never be visible to a client mid-brief.
 *
 * DELIVERY
 * ──────────────────────────────────────────────────────────────────────────
 * Events are queued and flushed in batches, because one HTTP request per
 * event would put a request on the wire for every keystroke-level
 * interaction. Three things force a flush:
 *
 *   1. the queue reaching BATCH_SIZE,
 *   2. a FLUSH_MS timer, so a quiet session still reports,
 *   3. the page going away — via sendBeacon, which is the only thing a
 *      browser guarantees to deliver during unload. `scope_abandoned` fires
 *      at exactly that moment and is the single most important event here,
 *      so losing the last batch would lose the thing we most want.
 *
 * Idempotency is the server's job via `event_uid`; this side may safely
 * resend. It does NOT retry indefinitely — see flush().
 */

interface QueuedEvent {
  event_uid: string;
  event: string;
  distinct_id: string | null;
  actor_role: string;
  session_id: string;
  project_id: string | null;
  properties: EventProperties;
  occurred_at: number;
}

const BATCH_SIZE = 20;
const FLUSH_MS = 10_000;
/** Hard ceiling so a long offline spell cannot grow the queue without bound. */
const MAX_QUEUE = 500;

export class SseProvider implements AnalyticsProvider {
  readonly name = 'sse';

  private ready = false;
  private endpoint = '';
  private apiKey = '';

  private queue: QueuedEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private distinctId: string | null = null;
  private role = 'anonymous';
  private readonly sessionId = this.uid();
  /** Set after a flush fails hard, so we stop talking to a dead endpoint. */
  private disabled = false;

  isReady(): boolean {
    return this.ready;
  }

  init(): void {
    const base = (environment as any)?.sseBaseUri;
    if (!base || (environment as any)?.analytics?.sseSink === false) return;
    try {
      this.endpoint = `${String(base).replace(/\/+$/, '')}/analytics/events`;
      this.apiKey = (environment as any)?.sseApiKey || '';
      this.ready = true;

      this.timer = setInterval(() => this.flush(false), FLUSH_MS);

      // `visibilitychange` -> hidden, not `unload`: unload handlers are not
      // run at all on mobile Safari and are being removed elsewhere, whereas
      // hidden fires on tab switch, app backgrounding and close. pagehide is
      // kept as a belt-and-braces second trigger; a duplicate batch is free
      // because the server dedupes on event_uid.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush(true);
      });
      window.addEventListener('pagehide', () => this.flush(true));
    } catch {
      this.ready = false;
    }
  }

  identify(identity: UserIdentity): void {
    // Flush FIRST: anything queued happened as the previous identity (usually
    // anonymous), and relabelling it here would attribute a logged-out
    // visitor's actions to whoever logged in next.
    this.flush(false);
    this.distinctId = identity?.distinctId || null;
    this.role = identity?.role || 'anonymous';
  }

  /**
   * Intentionally a no-op.
   *
   * User properties are a Mixpanel People concept. The equivalent here is the
   * domain record itself — the dashboard already reads a vendor's bio/rates
   * state from the contributor DB, which is the truth. Mirroring them into
   * the sink would create a second, staler copy for no gain, and those
   * property bags carry email and phone, which this sink deliberately does
   * not store.
   */
  setUserProperties(_props: UserProperties): void {
    /* see above */
  }

  track(event: string, properties?: EventProperties): void {
    if (!this.ready || this.disabled) return;
    try {
      const props = { ...(properties || {}) };
      // Promoted to a column server-side: every journey query filters on it,
      // and JSON extraction cannot use an index.
      const projectId = this.firstString(props, [
        'project_id', 'projectId', 'project_code',
      ]);

      this.queue.push({
        event_uid: this.uid(),
        event,
        distinct_id: this.distinctId,
        actor_role: String(props['actor_role'] ?? this.role),
        session_id: this.sessionId,
        project_id: projectId,
        properties: props,
        occurred_at: Date.now(),
      });

      if (this.queue.length >= MAX_QUEUE) {
        // Drop the OLDEST. A queue this long means a long outage, and the
        // recent events are the ones still worth having.
        this.queue.splice(0, this.queue.length - MAX_QUEUE);
      }
      if (this.queue.length >= BATCH_SIZE) this.flush(false);
    } catch {
      /* never let measurement break the thing being measured */
    }
  }

  trackRoute(path: string, title?: string): void {
    // Deliberately NOT sent. Route changes are high-volume and GA4 already
    // owns pageviews; storing one row per navigation would dwarf the
    // meaningful events in this table and make the journey view unreadable
    // without teaching every query to filter them out.
    void path;
    void title;
  }

  reset(): void {
    // Flush before detaching, or the final events of a session are attributed
    // to nobody — including the logout itself.
    this.flush(true);
    this.distinctId = null;
    this.role = 'anonymous';
  }

  // ── internals ──────────────────────────────────────────────────────────

  private flush(viaBeacon: boolean): void {
    if (!this.ready || this.disabled || !this.queue.length) return;

    const batch = this.queue.splice(0, this.queue.length);
    const body = JSON.stringify({ events: batch });

    try {
      if (viaBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        // sendBeacon cannot set headers, so the key rides as a query param —
        // the backend's middleware accepts `?api_key=` for exactly this case.
        // A Blob with an explicit type keeps it a simple request, so the
        // browser does not need a preflight it has no time to make.
        const url = `${this.endpoint}?api_key=${encodeURIComponent(this.apiKey)}`;
        const ok = navigator.sendBeacon(
          url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        // Beacon refused (payload too large, or queue full). Put them back so
        // the next timed flush tries properly over fetch.
        if (!ok) this.requeue(batch);
        return;
      }

      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey },
        body,
        keepalive: true,
      }).then((res) => {
        // 4xx is OUR bug (bad shape, too large) and resending cannot fix it —
        // retrying would loop forever against an endpoint that will never
        // accept this batch. 5xx and network errors are transient, so those
        // go back on the queue.
        if (!res.ok && res.status >= 500) this.requeue(batch);
      }).catch(() => this.requeue(batch));
    } catch {
      this.requeue(batch);
    }
  }

  private requeue(batch: QueuedEvent[]): void {
    try {
      this.queue = batch.concat(this.queue).slice(-MAX_QUEUE);
    } catch {
      /* give up on this batch rather than grow unbounded */
    }
  }

  private firstString(
    props: EventProperties, keys: string[],
  ): string | null {
    for (const k of keys) {
      const v = props[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    }
    return null;
  }

  private uid(): string {
    try {
      const c: any = (globalThis as any).crypto;
      if (c?.randomUUID) return c.randomUUID();
    } catch { /* fall through */ }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}
